export const DECK_PACE_TIERS = Object.freeze([
  Object.freeze({
    id: 'little',
    label: 'Little',
    minutes: 5,
    min_target: 1,
    max_target: 2,
    default_target: 2,
  }),
  Object.freeze({
    id: 'medium',
    label: 'Medium',
    minutes: 10,
    min_target: 2,
    max_target: 4,
    default_target: 3,
  }),
  Object.freeze({
    id: 'more',
    label: 'More',
    minutes: 15,
    min_target: 3,
    max_target: 5,
    default_target: 4,
  }),
  Object.freeze({
    id: 'a_lot',
    label: 'A lot',
    minutes: 25,
    min_target: 6,
    max_target: 6,
    default_target: 6,
  }),
]);

function parseDateString(value, fieldName) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new RangeError(`${fieldName} must use YYYY-MM-DD`);
  }
  const [year, month, day] = value.split('-').map(Number);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new RangeError(`${fieldName} must be a valid date`);
  }
  return timestamp;
}

export function getTodayDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getInclusiveDayCount(startDate, dueDate) {
  const startTimestamp = parseDateString(startDate, 'Start date');
  const dueTimestamp = parseDateString(dueDate, 'Due date');
  if (dueTimestamp < startTimestamp) {
    throw new RangeError('Due date cannot be before today');
  }
  return Math.floor((dueTimestamp - startTimestamp) / 86400000) + 1;
}

export function isValidDueDate(dueDate, today = getTodayDateString()) {
  if (dueDate === null || dueDate === '') {
    return true;
  }
  try {
    getInclusiveDayCount(today, dueDate);
    return true;
  } catch (error) {
    return false;
  }
}

export function getPaceTier(tierId) {
  const tier = DECK_PACE_TIERS.find(option => option.id === tierId);
  if (!tier) {
    throw new RangeError(`Unknown deck pace tier: ${tierId}`);
  }
  return tier;
}

export function createPace(tierId, target) {
  if (tierId === 'custom') {
    if (!Number.isInteger(target) || target < 1) {
      throw new RangeError('Custom pace target must be a positive integer');
    }
    return {
      tier: 'custom',
      minutes_per_day: null,
      verses_per_day: target,
    };
  }

  const tier = getPaceTier(tierId);
  if (
    !Number.isInteger(target)
    || target < tier.min_target
    || target > tier.max_target
  ) {
    throw new RangeError(
      `${tier.label} pace must be ${tier.min_target}-${tier.max_target} verses per day`
    );
  }
  return {
    tier: tier.id,
    minutes_per_day: tier.minutes,
    verses_per_day: target,
  };
}

export function recommendDeckPace(verseCount, dueDate, today = getTodayDateString()) {
  if (!Number.isInteger(verseCount) || verseCount < 1) {
    throw new RangeError('Verse count must be a positive integer');
  }
  const days = getInclusiveDayCount(today, dueDate);
  const target = Math.ceil(verseCount / days);
  const tier = DECK_PACE_TIERS.find(option => target === option.default_target)
    || DECK_PACE_TIERS.find(
      option => target >= option.min_target && target <= option.max_target
    );

  if (!tier) {
    return {
      days,
      target,
      tier: 'custom',
      label: 'Custom',
      minutes: null,
      is_custom: true,
    };
  }

  return {
    days,
    target,
    tier: tier.id,
    label: tier.label,
    minutes: tier.minutes,
    is_custom: false,
  };
}
